import { Icon } from '@league/ui';
import { Link, Outlet } from 'react-router-dom';

import type { Actor } from '../lib/auth';
import { Rail, type RailGroup, UserChip } from './Rail';

import { DIRECTOR_SCREENING_IN_V1, KIT_IN_V1 } from '@league/shared/features';
import { PageErrorBoundary } from '../components/PageErrorBoundary';

/**
 * HQ. The same rail, grouped — twenty-odd destinations in a flat list is a wall, and these fall
 * into five honest categories rather than five invented ones.
 */
const GROUPS: RailGroup[] = [
  { items: [{ to: '/hq', label: 'Dashboard', icon: 'dashboard', end: true }] },
  {
    title: 'Leagues',
    items: [
      { to: '/hq/leagues', label: 'All leagues', icon: 'league' },
      { to: '/hq/markets', label: 'Markets & venues', icon: 'market' },
    ],
  },
  {
    title: 'People',
    items: [
      { to: '/hq/directors', label: 'Directors', icon: 'directors' },
      /*
        Absent while screening is off — see `@league/shared/features`. A nav entry to a board that
        can never gain a row is a standing invitation to wonder what is broken.
      */
      ...(DIRECTOR_SCREENING_IN_V1
        ? [{ to: '/hq/applications', label: 'Applications', icon: 'applications' as const }]
        : []),
      { to: '/hq/players', label: 'Players', icon: 'players' },
    ],
  },
  {
    title: 'Operations',
    items: [
      /* Shipping and the tile fleet are not part of V1 — see `features.ts`. */
      ...(KIT_IN_V1
        ? [
            { to: '/hq/fulfillment', label: 'Fulfillment', icon: 'fulfillment' as const },
            { to: '/hq/tiles', label: 'Tile fleet', icon: 'tiles' as const },
          ]
        : []),
      { to: '/hq/trial-codes', label: 'Trial codes', icon: 'tasks' },
      { to: '/hq/tasks', label: 'Tasks', icon: 'tasks' },
      { to: '/hq/notes', label: 'Notes inbox', icon: 'notes' },
      { to: '/hq/quality', label: 'Quality & incidents', icon: 'quality' },
      { to: '/hq/audit', label: 'Audit', icon: 'audit' },
    ],
  },
  {
    title: 'Content',
    items: [
      { to: '/hq/templates', label: 'Email templates', icon: 'templates' },
      { to: '/hq/playbook', label: 'Playbook & checklists', icon: 'playbook' },
    ],
  },
];

/**
 * Settings is pinned to the bottom rather than being the fourteenth item in the list, and that is
 * not only the spec's layout — the rail is long enough to scroll on a 900px laptop, so an unpinned
 * Settings sits below the fold on the most common screen in the product.
 */
const SETTINGS: RailGroup[] = [
  { items: [{ to: '/hq/settings', label: 'Settings', icon: 'settings' }] },
];

export function CommissionerShell({
  actor,
  brand,
  onSignOut,
}: {
  actor: Actor;
  brand: string;
  onSignOut: () => void;
}) {
  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <Rail
        brand={brand}
        groups={GROUPS}
        header={<ViewingAs />}
        pinned={SETTINGS}
        footer={<UserChip name={actor.label} role={actor.role} onSignOut={onSignOut} />}
      />

      <main className="min-w-0 flex-1">
        <PageErrorBoundary><Outlet /></PageErrorBoundary>
      </main>
    </div>
  );
}

/**
 * "Viewing as" — the switch from running everything to running Denver.
 *
 * Courtney directs her own league as well as overseeing every other one, and without this she would
 * have to sign out of her own platform to do her own league session. The director routes already
 * admit her (`requireDirector` lets a commissioner through on purpose), so this is a link, not a
 * session change — there is no impersonation, no second identity, and nothing to get stuck in.
 */
function ViewingAs() {
  return (
    <Link
      to="/director"
      className="flex w-full items-center justify-between gap-2 rounded border border-on-deep/15 px-3 py-2.5 text-left transition-colors hover:bg-on-deep/14"
    >
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.09em] text-on-deep-2">
          Viewing as
        </span>
        <span className="block truncate text-[13px] font-semibold text-on-deep">
          Commissioner
        </span>
      </span>
      <Icon name="chevronRight" size={16} className="text-on-deep-2" />
    </Link>
  );
}

/**
 * The way back, shown in the director shell only when a commissioner is standing in it. A director
 * never sees it, because there is nowhere for them to return to.
 */
export function BackToHq() {
  return (
    <Link
      to="/hq"
      className="flex w-full items-center gap-2 rounded border border-on-deep/15 px-3 py-2.5 text-[13px] font-semibold text-on-deep transition-colors hover:bg-on-deep/14"
    >
      <Icon name="chevronRight" size={16} className="rotate-180 text-on-deep-2" />
      Back to HQ
    </Link>
  );
}
